import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CreateSharedLinkInput } from "@shared/validation/sharedLink.schema";
import type { EffectiveVisibleSections } from "@shared/lib/visibleSections";
import { apiClient, ApiError } from "./client";

export interface SharedLinkSummary {
  id: string;
  childId: string;
  tokenPrefix: string;
  caregiverName: string;
  caregiverEmail: string | null;
  /** What the link opens today: `pickup` is absent while the switch is off. */
  visibleSections: EffectiveVisibleSections;
  expiresAt: string | null;
  isActive: boolean;
  lastAccessedAt: string | null;
  accessCount: number;
  createdAt: string;
  revokedAt: string | null;
  acknowledgedAt: string | null;
}

export interface CreateSharedLinkResponse {
  link: SharedLinkSummary;
  /** Raw share token — returned ONCE on create. Never fetched again. */
  token: string;
}

export interface ShareLinkEvent {
  id: number;
  accessedAt: string;
  action:
    | "resolve"
    | "denied_inactive"
    | "denied_expired"
    | "denied_invalid_token"
    | "rate_limited";
  httpStatus: number;
}

const linksKey = (childId: string) => ["shared-links", childId] as const;
const eventsKey = (linkId: string) => ["shared-links", linkId, "events"] as const;
/** Every per-child list and events query. */
const allLinksKey = ["shared-links"] as const;
/** The dashboard's Active links tile count and popup list (useDashboardOverview). */
const dashboardKey = ["dashboard"] as const;

export function useSharedLinks(childId: string | undefined) {
  return useQuery<SharedLinkSummary[], ApiError>({
    queryKey: childId ? linksKey(childId) : ["shared-links", "__none__"],
    queryFn: () => {
      if (!childId) throw new Error("useSharedLinks: childId required");
      return apiClient<SharedLinkSummary[]>(
        `/api/shared-links?childId=${encodeURIComponent(childId)}`,
      );
    },
    enabled: Boolean(childId),
  });
}

export function useCreateSharedLink(childId: string) {
  const queryClient = useQueryClient();
  return useMutation<CreateSharedLinkResponse, ApiError, CreateSharedLinkInput>({
    mutationFn: (vars) =>
      apiClient<CreateSharedLinkResponse>("/api/shared-links", {
        method: "POST",
        body: JSON.stringify(vars),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: linksKey(childId) });
    },
  });
}

export function useRevokeSharedLink(childId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ revoked: true }, ApiError, string, { previous?: SharedLinkSummary[] }>({
    mutationFn: (linkId) =>
      apiClient<{ revoked: true }>(`/api/shared-links/${linkId}`, { method: "DELETE" }),
    // Optimistic: flip the link to revoked immediately, roll back on error.
    onMutate: async (linkId) => {
      await queryClient.cancelQueries({ queryKey: linksKey(childId) });
      const previous = queryClient.getQueryData<SharedLinkSummary[]>(linksKey(childId));
      if (previous) {
        queryClient.setQueryData<SharedLinkSummary[]>(
          linksKey(childId),
          previous.map((l) => (l.id === linkId ? { ...l, isActive: false } : l)),
        );
      }
      return { previous };
    },
    onError: (_err, _linkId, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(linksKey(childId), ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: linksKey(childId) });
      // The Active links tile and popup count this link too.
      queryClient.invalidateQueries({ queryKey: dashboardKey });
    },
  });
}

/**
 * "Delete" in the dashboard's Active links popup: the server revokes the link
 * (if it still works) and hides it from every list. It is never hard-deleted —
 * its access history stays. Refreshes every link list and the dashboard tile.
 */
export function useDismissSharedLink() {
  const queryClient = useQueryClient();
  return useMutation<{ dismissed: true }, ApiError, string>({
    mutationFn: (linkId) =>
      apiClient<{ dismissed: true }>(`/api/shared-links/${encodeURIComponent(linkId)}/dismiss`, {
        method: "POST",
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: allLinksKey });
      queryClient.invalidateQueries({ queryKey: dashboardKey });
    },
  });
}

export function useLinkAccessEvents(linkId: string | undefined) {
  return useQuery<ShareLinkEvent[], ApiError>({
    queryKey: linkId ? eventsKey(linkId) : ["shared-links", "__none__", "events"],
    queryFn: () => {
      if (!linkId) throw new Error("useLinkAccessEvents: linkId required");
      return apiClient<ShareLinkEvent[]>(`/api/shared-links/${linkId}/events`);
    },
    enabled: Boolean(linkId),
  });
}
